import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../Header/Header';
import ResearchProjectSidebar from './ResearchProjectSidebar';
import ResearchProjectSalaryTable from './ResearchProjectSalaryTable';
import ResearchProjectReportsSection from './ResearchProjectReportsSection';
import CompaniesTable from './CompaniesTable';
import PositionsTable from './PositionsTable';
import './ResearchProjectPage.css';
import { getProjectById } from '../../services/projectService';

const ResearchProjectPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [project, setProject] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [files, setFiles] = useState<any[]>([]);
  const [activeSection, setActiveSection] = useState<'analytics' | 'dashboard'>('analytics');
  const [projectDataJson, setProjectDataJson] = useState<any | null>(null);
  const [jsonLoading, setJsonLoading] = useState(false);
  const [jsonError, setJsonError] = useState<string | null>(null);

  useEffect(() => {
    const fetchProject = async () => {
      setLoading(true);
      setError(null);
      const data = await getProjectById(id!);
      if (!data?.project) {
        setError('Проект не найден');
        setLoading(false);
        return;
      }
      setProject(data.project);
      setFiles(data.files || []);
      setLoading(false);
    };
    if (id && user) fetchProject();
  }, [id, user]);

  // Загружаем содержимое project-data.json, если файл есть
  useEffect(() => {
    // Находим файл project-data.json или project_data.json
    const fileObj = files.find(f => {
      const name = (f.file_name || '').trim().toLowerCase();
      return name === 'project-data.json' || name === 'project_data.json';
    });
    if (!fileObj) {
      setProjectDataJson(null);
      setJsonError(null);
      setJsonLoading(false);
      return;
    }
    const filePath = fileObj.file_path;
    if (!filePath) {
      setProjectDataJson(null);
      setJsonError('Не указан путь к файлу');
      setJsonLoading(false);
      return;
    }
    setJsonLoading(true);
    setJsonError(null);
    // Читаем JSON по прямому URL
    (async () => {
      if (!filePath.startsWith('http://') && !filePath.startsWith('https://')) {
        setProjectDataJson(null);
        setJsonError('Файл не содержит прямую ссылку');
        setJsonLoading(false);
        return;
      }
      try {
        const response = await fetch(filePath);
        if (!response.ok) throw new Error('Ошибка загрузки файла');
        const json = await response.json();
        setProjectDataJson(json);
        setJsonLoading(false);
        setJsonError(null);
        console.log('project-data.json успешно загружен:', json);
      } catch (e) {
        setProjectDataJson(null);
        setJsonError('Ошибка чтения содержимого файла');
        setJsonLoading(false);
        console.error('Ошибка чтения project-data.json:', e);
      }
    })();
  }, [files]);

  if (loading) return <div style={{ padding: 32 }}>Загрузка...</div>;
  if (error) return <div style={{ padding: 32, color: '#e74c3c' }}>{error}</div>;
  if (!project) return null;

  const unsupportedType = project.type !== 'research';

  return (
    <div className="research-project-page">
      <Header user={user} onLogout={signOut} onDashboardClick={() => navigate('/workspace')} hideDashboardAndLogoutButtons />
      <div className="research-project-content">
        <aside className="research-project-sidebar">
          <ResearchProjectSidebar activeSection={activeSection} setActiveSection={setActiveSection} />
        </aside>
        <main className="research-project-main">
          <div className="main-content">
            {unsupportedType && (
              <div style={{ color: '#e74c3c', marginBottom: 16, fontSize: 16, fontWeight: 500 }}>
                Тип проекта не поддерживается
              </div>
            )}
            {!unsupportedType && (
              <>
                <h1 style={{ 
                  fontSize: 28, 
                  fontWeight: 700, 
                  color: '#1a237e', 
                  marginBottom: 8,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12
                }}>
                  <span style={{ fontSize: '32px', lineHeight: 1 }}>🔬</span>
                  {project.name}
                </h1>
                <div style={{ color: '#555', marginBottom: 16 }}>{project.description}</div>
                <div style={{ marginBottom: 16 }}>
                  <span style={{ fontWeight: 500, color: '#283593' }}>Тип:</span> Исследование
                </div>
                <div style={{ marginBottom: 16 }}>
                  <span style={{ fontWeight: 500, color: '#283593' }}>Дата создания:</span> {new Date(project.created_at).toLocaleDateString('ru-RU')}
                </div>
                <div style={{ marginBottom: 16 }}>
                  <span style={{ fontWeight: 500, color: '#283593' }}>Статус проекта:</span> {
                    projectDataJson?.project_info?.status ? (
                      projectDataJson.project_info.status === 'completed' ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                          <i className="fas fa-check-circle" style={{ color: '#4caf50', fontSize: '16px' }}></i>
                          Выполнено
                        </span>
                      ) : (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                          <i className="fas fa-hourglass-half" style={{ color: '#1a237e', fontSize: '16px' }}></i>
                          {projectDataJson.project_info.status}
                        </span>
                      )
                    ) : (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        <i className="fas fa-question-circle" style={{ color: '#9e9e9e', fontSize: '16px' }}></i>
                        Не определен
                      </span>
                    )
                  }
                </div>

                {files.some(f => {
                  const name = (f.file_name || '').trim().toLowerCase();
                  return name === 'project-data.json' || name === 'project_data.json';
                }) && (
                  <>
                    {jsonLoading && <div style={{ background: '#f5f7fa', borderRadius: 8, padding: 20, marginBottom: 16 }}>Загрузка компаний...</div>}
                    {jsonError && <div style={{ background: '#f5f7fa', borderRadius: 8, padding: 20, marginBottom: 16, color: '#e74c3c' }}>{jsonError}</div>}
                    {projectDataJson && Array.isArray(projectDataJson.companies) && projectDataJson.companies.length > 0 ? (
                      <CompaniesTable companies={projectDataJson.companies} />
                    ) : projectDataJson && Array.isArray(projectDataJson.companies) && projectDataJson.companies.length === 0 ? (
                      <div style={{ background: '#f5f7fa', borderRadius: 8, padding: 20, marginBottom: 16, color: '#555' }}>Нет компаний в файле</div>
                    ) : null}
                                          {jsonLoading && <div style={{ background: '#f5f7fa', borderRadius: 8, padding: 20, marginBottom: 16 }}>Загрузка должностей...</div>}
                      {jsonError && <div style={{ background: '#f5f7fa', borderRadius: 8, padding: 20, marginBottom: 16, color: '#e74c3c' }}>{jsonError}</div>}
                      {projectDataJson && Array.isArray(projectDataJson.positions) && projectDataJson.positions.length > 0 ? (
                        <PositionsTable positions={projectDataJson.positions} />
                      ) : projectDataJson && Array.isArray(projectDataJson.positions) && projectDataJson.positions.length === 0 ? (
                        <div style={{ background: '#f5f7fa', borderRadius: 8, padding: 20, marginBottom: 16, color: '#555' }}>Нет должностей в файле</div>
                      ) : null}
                    <div style={{ borderRadius: 8, padding: 20, marginBottom: 16 }}>
                      <h2 style={{ 
                        fontSize: 24, 
                        fontWeight: 600, 
                        color: '#1a237e', 
                        margin: 0,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8
                      }}>
                        <i className="fas fa-chart-line" style={{ fontSize: '22px' }}></i>
                        Аналитика
                      </h2>
                      <ResearchProjectSalaryTable 
                        companies={projectDataJson && Array.isArray(projectDataJson.companies) ? projectDataJson.companies : []} 
                      />
                    </div>
                    <ResearchProjectReportsSection projectId={id!} />
                  </>
                )}
              </>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default ResearchProjectPage; 